import { PlanOptionNameEnum } from "@platform/openapi-types";
import {
  Column,
  DefaultMenuItem,
  GetMainMenuItemsParams,
  MenuItemDef,
  ProvidedColumnGroup,
} from "ag-grid-community";

import { DeletePlanDialogData } from "../types/dialog.types";
import {
  extractColumnContext,
  extractColumnGroupContext,
} from "../utilities/buildPlanGroupColumns";

type PlanLevelMenuCallbacks = {
  openDeletePlanLevelDialog: (params: DeletePlanDialogData) => void;
  onAddPlanToGroup?: (planGroupId: string, currentSize: number) => void;
  onDuplicatePlanInGroup?: (
    planGroupId: string,
    benefitPlanId: string,
    currentSize: number,
  ) => void;
};

type ColumnInfo = {
  planHeaderName: string;
  planGroupId?: string;
  benefitPlanId?: string;
  optionName?: PlanOptionNameEnum;
  identifier?: string;
  size?: number;
};

const extractColumnInfo = (
  columnGroup: ProvidedColumnGroup | null,
  column: Column | null,
): ColumnInfo => {
  if (columnGroup) {
    const columnContext = extractColumnGroupContext(columnGroup);
    return {
      planHeaderName: columnGroup.getColGroupDef()?.headerName || "",
      planGroupId: columnContext?.planGroupId,
      benefitPlanId:
        typeof columnContext?.benefitPlanId === "string"
          ? columnContext.benefitPlanId
          : undefined,
      optionName: columnContext?.optionName,
      identifier: columnContext?.identifier,
      size: columnContext?.size,
    };
  }

  if (column) {
    const columnContext = extractColumnContext(column);
    return {
      planHeaderName: column.getColDef()?.headerName || "",
      planGroupId: columnContext?.planGroupId,
      benefitPlanId:
        typeof columnContext?.benefitPlanId === "string"
          ? columnContext.benefitPlanId
          : undefined,
      optionName: columnContext?.optionName,
      identifier: columnContext?.identifier,
      size: columnContext?.size,
    };
  }

  return {
    planHeaderName: "",
    planGroupId: undefined,
    benefitPlanId: undefined,
    optionName: undefined,
    identifier: undefined,
    size: undefined,
  };
};

/**
 * Get AG Grid main menu items for plan-level columns
 */
export const getPlanLevelMenuItem = (
  params: GetMainMenuItemsParams,
  {
    openDeletePlanLevelDialog,
    onAddPlanToGroup,
    onDuplicatePlanInGroup,
  }: PlanLevelMenuCallbacks,
): (DefaultMenuItem | MenuItemDef)[] => {
  const { planHeaderName, planGroupId, benefitPlanId, size } =
    extractColumnInfo(params.columnGroup, params.column);

  if (!planGroupId || !benefitPlanId) {
    return [];
  }

  const items: (DefaultMenuItem | MenuItemDef)[] = [];

  if (onAddPlanToGroup && size !== undefined) {
    items.push({
      name: "New Plan",
      action: () => {
        onAddPlanToGroup(planGroupId, size);
      },
    });
  }

  if (onDuplicatePlanInGroup && size !== undefined) {
    items.push({
      name: "Duplicate Plan",
      action: () => {
        onDuplicatePlanInGroup(planGroupId, benefitPlanId, size);
      },
    });
  }

  // Deleting the only plan in a group would empty the group — that's what
  // Delete Plan Group is for. Offer Delete Plan only when other plans remain.
  if (size !== 1) {
    items.push({
      name: "Delete Plan",
      action: () => {
        openDeletePlanLevelDialog({
          planGroupId,
          planName: planHeaderName,
          benefitPlanId,
        });
      },
    });
  }

  return items;
};
